import { PopupButtonType, showPopup, Touchable } from "@kalyzee/kast-app-web-components";
import React, { useImperativeHandle, useRef } from "react";
import Table, { TableColumnType, TableConf, TableConfColumn, TableContentRef, TableStyle } from "../../../components/utils/Table";
import { useRender } from "../../../hooks/component";
import { useElementSize } from "../../../hooks/window";
import { OvenMediaAppOutputProfileEncodeVideo } from "../../../interfaces/context";
import styles from "./table-video-encoder.module.css";

export interface TableVideoEncoderData extends OvenMediaAppOutputProfileEncodeVideo {}

export interface TableVideoEncoderRef {
  render: () => void;
}

export interface TableVideoEncoderProps {
  data: TableVideoEncoderData[];
  onItemChecked?: (item: TableVideoEncoderData) => void;
  className?: string;
  style?: React.CSSProperties;
}
const TableVideoEncoder = React.forwardRef(
  ({ data, onItemChecked, className, style }: TableVideoEncoderProps, forwardRef: React.ForwardedRef<TableVideoEncoderRef | undefined>) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const render = useRender();
    const size = useElementSize(containerRef);

    useImperativeHandle(forwardRef, () => ({
      render,
    }));

    const generateConfiguration = () => {
      const columnConfiguration: TableConfColumn<TableVideoEncoderData>[] = [
        /* {
        type: TableColumnType.CHECKBOX,
        key: 'checked',
      }, */
        {
          type: TableColumnType.CLASSIC,
          key: "name",
          width: "15%",
          minWidth: "10rem",
          title: "Name" /* TRANSLATION */,
          header: { className: styles.tableHeaderCellName },
          item: { className: styles.tableCellName },
        },
        {
          type: TableColumnType.CLASSIC,
          key: "bypass",
          title: "Bypass" /* TRANSLATION */,
          width: "4rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "bypassifmatch",
          title: "Bypass if match" /* TRANSLATION */,
          width: "8rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "codec",
          title: "Codec" /* TRANSLATION */,
          width: "5rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "framerate",
          title: "Framerate" /* TRANSLATION */,
          width: "6rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "bitrate",
          title: "Bitrate (Mbps)" /* TRANSLATION */,
          width: "8rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "height",
          title: "Height" /* TRANSLATION */,
          width: "5rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "width",
          title: "Width" /* TRANSLATION */,
          width: "5rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "profile",
          title: "Profile" /* TRANSLATION */,
          width: "5rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "preset",
          title: "Preset" /* TRANSLATION */,
          width: "5rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "keyFrameInterval",
          title: "KeyFrame (s)" /* TRANSLATION */,
          width: "7rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "bFrames",
          title: "BFrame" /* TRANSLATION */,
          width: "5rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "threadCount",
          title: "Thread Count" /* TRANSLATION */,
          width: "7rem",
        },
      ];

      const tableConfiguration: TableConf<TableVideoEncoderData> = {
        columns: columnConfiguration,
        header: {
          className: styles.tableHeader,
          cell: {
            className: styles.tableHeaderCell,
          },
        },
        row: {
          className: styles.tableRow,
          cell: {
            className: styles.tableRowCell,
          },
        },
        content: { className: styles.tableContent },
        valueToShowIfUndefined: { value: "-", className: styles.tableUndefinedValue },
      };

      return tableConfiguration;
    };

    // Called when a value is changed. Checkboxes here
    const valueChanged = (value: any, columnKey: string, item: TableVideoEncoderData) => {
      if (columnKey === "checked") onItemChecked?.(item);
    };

    const customRenderCell = (element: JSX.Element | null, elementRef: TableContentRef, columnKey: string, item: TableVideoEncoderData) => {
      if (columnKey === "bypass") {
        if (item.bypass) {
          return <div className={styles.bypassYes}>✔</div>;
        }
        return <div className={styles.bypassFalse}>✗</div>;
      }
      if (columnKey === "bypassifmatch") {
        if (item.bypassIfMatch) {
          return (
            <Touchable
              className={styles.bypassifmatchYes}
              onPress={() => {
                showPopup({
                  content: (
                    <>
                      <div style={{ marginBottom: 10, minWidth: "200px", fontWeight: "bold" }}>{"Bypass if match:" /* TRANSLATION */}</div>
                      <div className={styles.bypassifmatchDetailsRow}>
                        <div>{`• codec: ` /* TRANSLATION */}</div>
                        <div>{`${item.bypassIfMatch?.codec ?? "-"}`}</div>
                      </div>
                      <div className={styles.bypassifmatchDetailsRow}>
                        <div>{`• width: ` /* TRANSLATION */}</div>
                        <div>{`${item.bypassIfMatch?.width ?? "-"}`}</div>
                      </div>
                      <div className={styles.bypassifmatchDetailsRow}>
                        <div>{`• height: ` /* TRANSLATION */}</div>
                        <div>{`${item.bypassIfMatch?.height ?? "-"}`}</div>
                      </div>
                      <div className={styles.bypassifmatchDetailsRow}>
                        <div>{`• SAR: ` /* TRANSLATION */}</div>
                        <div>{`${item.bypassIfMatch?.sar ?? "-"}`}</div>
                      </div>
                    </>
                  ),
                  buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                });
              }}
            >
              ✔
            </Touchable>
          );
        }
        return <div className={styles.bypassifmatchFalse}>✗</div>;
      }
      if (columnKey === "bitrate" && item.bitrate) {
        const bitrate = Number(item.bitrate ?? "0");
        const bps = Number.isNaN(bitrate) ? 0 : bitrate;
        const kbps = (bps / 1000).toFixed(2);
        const mbits = (bps / 1000000).toFixed(2);
        return (
          <Touchable
            className={styles.bitrate}
            onPress={() => {
              showPopup(
                {
                  content: (
                    <div style={{ minWidth: 300 }}>
                      <div style={{ marginBottom: 10, fontWeight: "bold" }}>{"Bitrate:" /* TRANSLATION */}</div>
                      <div>
                        <div style={{ display: "flex", flexDirection: "row", gap: 10, justifyContent: "space-between" }}>
                          <div>bits/s: </div>
                          <div style={{}}>{`${bps} bits/s`}</div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row", gap: 10, justifyContent: "space-between" }}>
                          <div>kbits/s: </div>
                          <div style={{}}>{`${kbps} kbits/s`}</div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row", gap: 10, justifyContent: "space-between" }}>
                          <div>Mbits/s: </div>
                          <div style={{}}>{`${mbits} Mbits/s`}</div>
                        </div>
                      </div>
                    </div>
                  ),
                  buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                },
                undefined,
                { displayHover: true }
              );
            }}
          >{`${mbits} Mbits/s`}</Touchable>
        );
      }
      return element;
    };

    const addCustomStyleOnCell = (columnKey: string, item: TableVideoEncoderData) => {
      const result: TableStyle = {};
      return result;
    };

    const addCustomStyleOnRow = (item: TableVideoEncoderData, currData: TableVideoEncoderData[], index: number) => {
      const result: TableStyle = {};
      return result;
    };

    const onRenderTableStarts = () => {};

    const onRenderTableEnded = () => {};

    const renderTable = () => (
      <Table
        className={styles.table}
        data={data}
        keyExtractor={(_, item) => `key-${item.name}`}
        configuration={generateConfiguration()}
        onRenderCellRow={customRenderCell}
        onStyleCellRow={addCustomStyleOnCell}
        onStyleRow={addCustomStyleOnRow}
        onChangeValue={valueChanged}
        onRenderStarts={() => onRenderTableStarts}
        onRenderEnded={onRenderTableEnded}
      />
    );

    const classes = [styles.container];
    if (className) classes.push(className);
    return (
      <div className={classes.join(" ")} style={style} ref={containerRef}>
        {renderTable()}
      </div>
    );
  }
);

export default TableVideoEncoder;
